import regex from '../modules/regex-rules.js';
import { customValidate, customMessages, restrictInput } from '../modules/custom-validation.js';
import { setLoadingState, getAlertHtml, toggleFields, resetForm } from '../modules/form-helpers.js';
import { slideUp, slideDown } from '../modules/animations.js';
import { errorPlacement, highlight, unhighlight } from '../modules/form-validation.js';

// Initializare campuri conditionate (alerta redirect LP panouri)
new toggleFields(document.getElementById('form-afm'));

const $ = window.jQuery;
const recaptcha = window.grecaptcha;

if (!$ || !$.fn || !$.fn.validate) {
	throw new Error('jQuery sau jQuery Validate nu sunt disponibile.');
}

if (!recaptcha || !recaptcha.ready) {
	throw new Error('reCAPTCHA nu e disponibil.');
}

window.dataLayer = window.dataLayer || [];

class Form {
	constructor({ formContainer, formName, formType, formCategory }) {
		if (!formContainer) {
			throw new Error('`formContainer` e null.');
		}

		if (!formContainer.dataset.fetchUrl) {
			throw new Error(`#${formContainer.id}: data-fetch-url e null.`);
		}

		if (!formName) {
			throw new Error('`formName` e null.');
		}

		if (!formType) {
			throw new Error('`formType` e null.');
		}

		if (!formCategory) {
			throw new Error('`formCategory` e null.');
		}

		this.form = formContainer;
		this.responseContainer = formContainer.querySelector('#response-message');

		if (!this.responseContainer) {
			throw new Error('#response-message e null.');
		}

		this.errorType = '';
		this.autoCloseTimer = 10000;

		// dataLayer related
		this.formDescriptor = {
			form_name: formName,
			form_type: formType,
			form_category: formCategory,
			form_url: window.location.href.split('?')[0],
		};

		this.init();
		this.validate();
		this.updateSubmitState();
	}

	init() {
		// Metode custom de validare
		$.validator.addMethod('alpha', customValidate.alpha);
		$.validator.addMethod('phone', customValidate.phone);

		// Restrictionari inputuri
		restrictInput('#telefon', regex.digits);
		restrictInput('#nume', regex.lettersSpacesHyphens);

		// Reguli de validare
		this.rules = {
			nume: {
				required: true,
				alpha: true,
				minlength: 3,
				maxlength: 50,
			},
			telefon: {
				required: true,
				phone: true,
			},
			email: {
				required: true,
				email: true,
			},
			varianta_pv: {
				required: true,
			},
			acord_gdpr: {
				required: true,
			},
		};

		// Mesaje eroare la validare
		this.messages = {
			nume: {
				required: customMessages.required('Nume și prenume'),
				alpha: customMessages.alpha('Nume și prenume'),
				minlength: customMessages.minlength('Nume și prenume'),
				maxlength: customMessages.maxlength('Nume și prenume'),
			},
			telefon: {
				required: customMessages.required('Telefon'),
				minlength: customMessages.minlength('Telefon', 10, 'cifre'),
				maxlength: customMessages.maxlength('Telefon', 10, 'cifre'),
				phone: customMessages.phone('Telefon'),
			},
			email: {
				required: customMessages.required('Email'),
				email: customMessages.email('Email'),
			},
			varianta_pv: {
				required: customMessages.radio('Alege varianta care ți se potrivește'),
			},
			acord_gdpr: {
				required: customMessages.gdpr(),
			},
		};

		document.addEventListener('change', this.updateSubmitState.bind(this));
	}

	/**
	 * Dezactiveaza butonul de submit daca se selecteaza "Vreau sa imi instalez panouri"
	 */
	updateSubmitState() {
		const radioValue = document.querySelector('[name="varianta_pv"]:checked')?.value;
		const submitBtn = document.getElementById('submit');

		if (!submitBtn) return;

		const shouldDisable = radioValue === 'vreau_panouri';
		submitBtn.disabled = shouldDisable;
	}

	async handleSubmit(form, event) {
		event?.preventDefault();

		const formData = new FormData(form);
		formData.append('action', 'write_afm_lead');

		const recaptchaPassed = recaptcha.getResponse();

		// GTM form_details
		const formDetails = `Varianta: ${formData.get('varianta_pv') || ''}`;

		if (recaptchaPassed) {
			setLoadingState('loading');

			const url = new URL(form.dataset.fetchUrl);
			url.searchParams.set('action', 'write_afm_lead');

			try {
				const response = await fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(Object.fromEntries(formData)),
				});

				const { success, data } = await response.json();

				this.errorType = !success ? 'fetch' : '';

				if (success) {
					this.responseContainer.innerHTML = getAlertHtml('success', data.responseTitle || 'Datele au fost trimise cu succes', data.response, 'mt-4 auto-close');
					this.responseContainer.style.setProperty('--autoclose', `${this.autoCloseTimer}ms`);
					setTimeout(() => {
						slideUp(this.responseContainer);
						this.responseContainer.style.removeProperty('--autoclose');
						this.responseContainer.querySelector('#response-message-inner')?.classList.remove('auto-close');
					}, this.autoCloseTimer);

					window.dataLayer.push({
						...this.formDescriptor,
						event: 'form_submitted',
						form_details: formDetails,
						lead: 'Succes',
						phone_number: `+4${formData.get('telefon')}`,
						email: formData.get('email'),
					});

					resetForm(this.form);
					this.updateSubmitState();
				} else {
					this.responseContainer.innerHTML = getAlertHtml('danger', data.responseTitle || 'Am întâmpinat o eroare', data.response);

					window.dataLayer.push({
						...this.formDescriptor,
						event: 'form_error',
						error_field: data.field || data.reason || 'Server',
						error_message: data.response,
					});
				}
			} catch (err) {
				console.error('Fetch error:', err.message);
				this.errorType = 'fetch';
				this.responseContainer.innerHTML = getAlertHtml('danger');
			} finally {
				setLoadingState();
				grecaptcha.reset();
				slideDown(this.responseContainer);
			}
		} else {
			this.errorType = 'recaptcha';
			this.responseContainer.innerHTML = getAlertHtml('danger', 'Bifează că nu ești robot.', 'Pentru a trimite formularul, e necesar să bifezi reCAPTCHA.');

			window.dataLayer.push({
				...this.formDescriptor,
				event: 'form_error',
				form_details: formDetails,
				lead: 'Captcha invalid',
				error_field: 'reCaptcha',
				error_message: 'reCaptcha nebifat',
			});

			slideDown(this.responseContainer);
		}
	}

	handleValidation(event, validator) {
		validator.errorList.forEach((error) => {
			window.dataLayer.push({
				...this.formDescriptor,
				event: 'form_error',
				error_field: error.element.name,
				error_message: error.message,
			});
		});
	}

	handleErrors(validator) {
		if (validator.numberOfInvalids() > 0) {
			this.errorType = 'validation';
			this.responseContainer.innerHTML = getAlertHtml('danger', 'Câmpurile marcate cu roșu conțin erori', 'Te rugăm să verifici și să reîncerci.');
			slideDown(this.responseContainer);
		} else {
			if (this.errorType === 'validation') {
				this.errorType = '';
				slideUp(this.responseContainer);
			}
		}
		validator.defaultShowErrors();
	}

	validate() {
		const self = this;

		$(this.form).validate({
			focusInvalid: false,
			submitHandler: async function (form, event) {
				await self.handleSubmit(form, event);
			},
			invalidHandler: function (event, validator) {
				self.handleValidation(event, validator);
			},
			showErrors: function () {
				self.handleErrors(this);
			},
			rules: this.rules,
			messages: this.messages,
			errorPlacement,
			highlight,
			unhighlight,
			errorClass: 'text-danger line-height-1-3 t-centi e-form-error',
		});
	}
}

recaptcha.ready(() => {
	new Form({
		formContainer: document.getElementById('form-afm'),
		formName: 'AFM',
		formType: 'lead',
		formCategory: 'Green',
	});
});
